import callGraphQLApi, {
  GQLApiDispatch,
  GQLApiGetState,
} from '@helpers/callGraphQLApi';

import { selectConfigEngines } from '../selectors';
import * as Actions from './actions';
import { readCachedAvailability, writeCachedAvailability } from './cache';

/**
 * `engines(ids:)` returns only the engines this caller can see, and `state`
 * says whether each is deployed. An id that comes back `active` can be run
 * here; one that is absent from the response, or in any other state, cannot.
 *
 * Asked by id rather than by category because the question is about the
 * specific engines the app would submit — a category that has SOME engine
 * deployed tells us nothing about whether the French one is among them.
 */
const availableEnginesQuery = `
  query redactAvailableEngines($ids: [ID!]) {
    engines(ids: $ids, limit: 500) {
      records {
        id
        state
      }
    }
  }
`;

interface AvailableEnginesResponse {
  engines: {
    records: ({ id: string; state: string | null } | null)[] | null;
  } | null;
}

/** aiWARE's EngineState for "deployed and active"; see the schema's enum. */
const DEPLOYED_STATE = 'active';

/**
 * Resolve which of `engineIds` this environment has, from the cache when it is
 * fresh and from the API otherwise.
 *
 * A thunk rather than a saga-side call so it can be `put` like the other
 * services in this module, and so the cache read happens on the same path as
 * the fetch — a cached answer dispatches the same SUCCESS the API would, and
 * nothing downstream can tell the two apart.
 */
export const checkEngineAvailability =
  (engineIds: readonly string[]) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    try {
      if (!engineIds.length) {
        return;
      }

      const { apiRoot } = selectConfigEngines(getState());

      const cached = readCachedAvailability(apiRoot, engineIds);
      if (cached) {
        dispatch(
          Actions.CHECK_ENGINE_AVAILABILITY_SUCCESS({ availableIds: cached })
        );
        return;
      }

      const response = await callGraphQLApi<AvailableEnginesResponse>({
        actionTypes: ['', '', ''],
        query: availableEnginesQuery,
        variables: { ids: [...engineIds] },
        dispatch,
        getState,
      });

      /**
       * No answer is not an empty answer.
       *
       * `callGraphQLApi` RESOLVES with undefined on more than one path — a
       * bailout, and a response whose errors are all `not_found` — so a
       * failed query does not necessarily throw, and the catch below cannot
       * be the only guard. Folding that into `|| []` made "we could not ask"
       * look exactly like "this environment has none of them": SUCCESS with
       * an empty answer, written to the cache, and every language gone from
       * every Select for 24 hours, surviving reload, with nothing in the app
       * to clear it.
       *
       * The optimistic contract this module is built on — unknown means offer
       * everything — only holds if an unknown is reported as one.
       */
      const records = response?.engines?.records;
      if (!records) {
        dispatch(
          Actions.CHECK_ENGINE_AVAILABILITY_FAILURE({
            error: new Error(
              'Engine availability query returned no records; availability is unknown'
            ),
          })
        );
        return;
      }

      const availableIds = records
        .filter(
          (record): record is { id: string; state: string | null } =>
            !!record && record.state === DEPLOYED_STATE
        )
        .map(({ id }) => id);

      /**
       * An empty answer is reported but never CACHED.
       *
       * It is a legitimate reply — an environment may genuinely have none of
       * these engines — so this session honours it. Persisting it is the part
       * that is not worth it: its cost if wrong is total (no language can be
       * picked at all, for a day), and the cost of re-asking is one query the
       * next time the app loads. The asymmetry is the whole argument.
       */
      if (availableIds.length) {
        writeCachedAvailability(apiRoot, engineIds, availableIds);
      }
      dispatch(Actions.CHECK_ENGINE_AVAILABILITY_SUCCESS({ availableIds }));
    } catch (error) {
      dispatch(Actions.CHECK_ENGINE_AVAILABILITY_FAILURE({ error }));
    }
  };
